"use client";

import { useCallback, useRef } from "react";

/**
 * SoundEngine — Web Audio API synthesized micro-sounds for UI interactions.
 * All sounds are procedurally generated (no audio files needed).
 * Provides a hook `useSoundEngine` that returns { playSound, soundEnabled, toggleSound }.
 */

export type SoundType = "click" | "nav" | "success" | "error" | "toggle";

/* Audio context is created lazily on first user interaction */
let audioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext {
  if (!audioCtx || audioCtx.state === "closed") {
    audioCtx = new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext)();
  }
  /* Resume if suspended (browser autoplay policy) */
  if (audioCtx.state === "suspended") {
    audioCtx.resume();
  }
  return audioCtx;
}

/**
 * Synthesize a short sound from scratch using oscillators + gain envelopes.
 */
function synthesize(type: SoundType): void {
  try {
    const ctx = getAudioCtx();
    const now = ctx.currentTime;

    /* Master gain — keep everything subtle */
    const master = ctx.createGain();
    master.gain.value = 0.08; // Very quiet
    master.connect(ctx.destination);

    switch (type) {
      case "click": {
        /* Short, crisp click — sine ping at 2400Hz, 60ms decay */
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(2400, now);
        osc.frequency.exponentialRampToValueAtTime(1800, now + 0.06);
        gain.gain.setValueAtTime(0.6, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
        osc.connect(gain);
        gain.connect(master);
        osc.start(now);
        osc.stop(now + 0.06);
        break;
      }

      case "nav": {
        /* Soft navigation hover — two quick tones like a gentle chime */
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(1200, now);
        osc.frequency.exponentialRampToValueAtTime(1800, now + 0.04);
        gain.gain.setValueAtTime(0.25, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
        osc.connect(gain);
        gain.connect(master);
        osc.start(now);
        osc.stop(now + 0.08);
        break;
      }

      case "success": {
        /* Rising two-tone chime — indicates completion */
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        const gain2 = ctx.createGain();

        osc1.type = "sine";
        osc1.frequency.value = 880;
        gain1.gain.setValueAtTime(0.4, now);
        gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
        osc1.connect(gain1);
        gain1.connect(master);
        osc1.start(now);
        osc1.stop(now + 0.12);

        osc2.type = "sine";
        osc2.frequency.value = 1320;
        gain2.gain.setValueAtTime(0, now);
        gain2.gain.linearRampToValueAtTime(0.4, now + 0.06);
        gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
        osc2.connect(gain2);
        gain2.connect(master);
        osc2.start(now + 0.06);
        osc2.stop(now + 0.18);
        break;
      }

      case "error": {
        /* Low buzz — square wave at 220Hz with fast tremolo */
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "square";
        osc.frequency.value = 220;
        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
        osc.connect(gain);
        gain.connect(master);
        osc.start(now);
        osc.stop(now + 0.15);
        break;
      }

      case "toggle": {
        /* Tick-tock — quick high blip for on/off toggling */
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(1600, now);
        osc.frequency.exponentialRampToValueAtTime(800, now + 0.05);
        gain.gain.setValueAtTime(0.35, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
        osc.connect(gain);
        gain.connect(master);
        osc.start(now);
        osc.stop(now + 0.05);
        break;
      }
    }
  } catch {
    /* Silently fail — audio is a nice-to-have, not critical */
  }
}

/**
 * Hook that provides sound playback + toggle.
 * Sound is OFF by default (opt-in) to respect user preferences.
 */
export function useSoundEngine() {
  const enabledRef = useRef(false);
  /* We use a ref to avoid re-renders when toggling sound,
   * but we also need a state value for the toggle UI in CommandCenter.
   * The parent component (AmbientWrapper) manages the state. */

  const playSound = useCallback((type: SoundType) => {
    if (enabledRef.current) {
      synthesize(type);
    }
  }, []);

  const setEnabled = useCallback((val: boolean) => {
    enabledRef.current = val;
    /* Eagerly create AudioContext on first enable (user gesture context) */
    if (val) {
      try {
        getAudioCtx();
      } catch {
        /* no-op */
      }
    }
  }, []);

  return { playSound, setEnabled, enabledRef };
}
